<script setup>
import { computed, reactive, ref, watch } from 'vue';

import FlowJsonNode from './FlowJsonNode.vue';
import { getInitialFlowScreen } from './flowJsonSchema';

const props = defineProps({
  schema: { type: Object, required: true },
  initialScreenId: { type: String, default: '' },
});

const emit = defineEmits(['close']);

const values = reactive({});
const history = ref([]);
const activeScreenId = ref('');
const completed = ref(false);

const activeScreen = computed(() =>
  props.schema.screens.find(screen => screen.id === activeScreenId.value)
);

const reset = () => {
  const initialScreen = getInitialFlowScreen(
    props.schema,
    props.initialScreenId
  );
  activeScreenId.value = initialScreen?.id || '';
  history.value = [];
  completed.value = false;
  Object.keys(values).forEach(key => delete values[key]);
};

watch(() => [props.schema, props.initialScreenId], reset, { immediate: true });

const updateValue = ({ name, value }) => {
  if (name) values[name] = value;
};

const handleAction = action => {
  if (action.name === 'navigate' && action.next?.name) {
    history.value.push(activeScreenId.value);
    activeScreenId.value = action.next.name;
    return;
  }

  if (action.name === 'complete' || action.name === 'data_exchange') {
    completed.value = true;
  }
};

const goBack = () => {
  const previous = history.value.pop();
  if (previous) activeScreenId.value = previous;
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div
    class="w-full max-w-[90%] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg"
  >
    <div class="flex items-center gap-2 bg-[#075E54] px-4 py-3 text-white">
      <button
        v-if="history.length"
        type="button"
        class="i-lucide-arrow-left size-5"
        aria-label="Voltar para a tela anterior"
        @click="goBack"
      />
      <div v-else class="i-lucide-workflow text-lg" />
      <div class="min-w-0 flex-1">
        <div class="truncate text-sm font-semibold">
          {{ activeScreen?.title || 'WhatsApp Flow' }}
        </div>
        <div class="truncate text-[10px] text-emerald-100">
          {{ activeScreen?.id }}
        </div>
      </div>
      <button
        type="button"
        class="i-lucide-x size-5"
        aria-label="Fechar prévia do Flow"
        @click="emit('close')"
      />
    </div>

    <div v-if="completed" class="space-y-3 p-5 text-center text-slate-800">
      <div class="i-lucide-circle-check mx-auto text-4xl text-emerald-600" />
      <div class="font-semibold">{{ $t('FLOW_PREVIEW.COMPLETED') }}</div>
      <p class="text-xs text-slate-500">
        {{ $t('FLOW_PREVIEW.ACTION_EXECUTED') }}
        {{
          activeScreen?.terminal
            ? $t('FLOW_PREVIEW.COMPLETE')
            : $t('FLOW_PREVIEW.DATA_EXCHANGE')
        }}{{ $t('COMMON.SENTENCE_PERIOD') }}
      </p>
      <button
        type="button"
        class="text-sm font-semibold text-emerald-700"
        @click="reset"
      >
        {{ $t('FLOW_PREVIEW.TEST_AGAIN') }}
      </button>
    </div>

    <div
      v-else-if="activeScreen"
      class="max-h-96 space-y-3 overflow-y-auto p-4"
    >
      <FlowJsonNode
        v-for="(node, index) in activeScreen.layout.children"
        :key="node.name || `${node.type}-${index}`"
        :node="node"
        :values="values"
        @action="handleAction"
        @update="updateValue"
      />
    </div>
  </div>
  <!-- eslint-enable vue/no-bare-strings-in-template -->
</template>
